import React, { useState } from "react";
import PropTypes from "prop-types";
import { NavLink, Link } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import {
  brandRankingUrl,
  brandUrl,
  collectionsUrl,
  searchUrlByGender,
  searchUrlByCategoryName,
} from "../../util/url";
import "./NavigationSubMenu.css";

const GET_NAV_DATA = gql`
  query getNavData {
    getCategoriesCached(limit: 1000) {
      name
      subcategories {
        id
        name
        gender
      }
    }
    getPopularKeywordsByFilterCached(limit: 30) {
      topTrendingBrands1m {
        id
        name
      }
    }
  }
`;

function ChevronDown() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

const CATEGORY_COLUMN_GROUPS = {
  라이프: [
    {
      title: "홈 & 오브젝트",
      names: ["오브젝트", "소품", "테이블웨어", "가구", "러그/패브릭"],
    },
    {
      title: "아트 & 미디어",
      names: ["포스터/아트", "잡지/책", "음반(CD/LP)"],
    },
    { title: "테크 & 음향", names: ["카메라", "음향"] },
  ],
  굿즈: [
    {
      title: "굿즈",
      names: ["TCG 카드", "인형/봉제", "피규어", "음악/아티스트 굿즈"],
    },
  ],
};

function CategoryMegaMenu({ categoryName, categories, onLinkClick }) {
  const category = categories.find((cat) => cat.name === categoryName);
  if (!category || !category.subcategories?.length) return null;

  const subMap = Object.fromEntries(
    category.subcategories.map((sub) => [sub.name, sub]),
  );
  const columnGroups = CATEGORY_COLUMN_GROUPS[categoryName];

  if (columnGroups) {
    const cols = columnGroups
      .map((group) => ({
        ...group,
        subs: group.names.map((name) => subMap[name]).filter(Boolean),
      }))
      .filter((col) => col.subs.length > 0);

    if (!cols.length) return null;

    return (
      <div className="Navigation-submenu-megamenu">
        <div className="Navigation-submenu-megamenu-inner">
          {cols.map((col) => (
            <div key={col.title} className="Navigation-submenu-megamenu-col">
              <div className="Navigation-submenu-megamenu-col-title">
                {col.title}
              </div>
              {col.subs.map((sub) => (
                <Link
                  key={sub.id}
                  to={searchUrlByCategoryName(categoryName, sub.id)}
                  onClick={onLinkClick}
                >
                  {sub.name}
                </Link>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="Navigation-submenu-megamenu">
      <div className="Navigation-submenu-megamenu-inner">
        <div className="Navigation-submenu-megamenu-col">
          <div className="Navigation-submenu-megamenu-col-title">
            {categoryName}
          </div>
          {category.subcategories.map((sub) => (
            <Link
              key={sub.id}
              to={searchUrlByCategoryName(categoryName, sub.id)}
              onClick={onLinkClick}
            >
              {sub.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

CategoryMegaMenu.propTypes = {
  categoryName: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      subcategories: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          name: PropTypes.string.isRequired,
          gender: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  onLinkClick: PropTypes.func,
};

function GenderMegaMenu({ gender, categories, onLinkClick }) {
  if (!categories.length) return null;

  const EXCLUDED_CATEGORIES = ["라이프", "굿즈"];

  const filteredCategories = categories
    .filter((cat) => !EXCLUDED_CATEGORIES.includes(cat.name))
    .map((cat) => ({
      ...cat,
      subcategories:
        cat.subcategories?.filter((sub) =>
          gender === "MEN"
            ? sub.gender === "MEN" || sub.gender === "UNISEX"
            : sub.gender === "WOMEN" || sub.gender === "UNISEX",
        ) ?? [],
    }))
    .filter((cat) => cat.subcategories.length > 0);

  if (!filteredCategories.length) return null;

  return (
    <div className="Navigation-submenu-megamenu">
      <div className="Navigation-submenu-megamenu-inner Navigation-submenu-megamenu-inner--gender">
        {filteredCategories.map((cat) => (
          <div key={cat.name} className="Navigation-submenu-megamenu-col">
            <div className="Navigation-submenu-megamenu-col-title">
              {cat.name}
            </div>
            {cat.subcategories.map((sub) => (
              <Link key={sub.id} to={searchUrlByGender(gender, sub.id)} onClick={onLinkClick}>
                {sub.name}
              </Link>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

GenderMegaMenu.propTypes = {
  gender: PropTypes.oneOf(["MEN", "WOMEN"]).isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      subcategories: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          name: PropTypes.string.isRequired,
          gender: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  onLinkClick: PropTypes.func,
};

export default function NavigationSubMenu() {
  const { data: navData } = useQuery(GET_NAV_DATA);
  const [isForceClosing, setIsForceClosing] = useState(false);

  const handleLinkClick = () => setIsForceClosing(true);
  const handleItemMouseLeave = () => setIsForceClosing(false);

  const categories = navData?.getCategoriesCached || [];
  const popularBrands =
    navData?.getPopularKeywordsByFilterCached?.topTrendingBrands1m || [];

  return (
    <div className={`Navigation-submenu${isForceClosing ? " Navigation-submenu--force-close" : ""}`}>
      <div className="Navigation-submenu-inner">
        {/* 멘즈웨어 */}
        <div className="Navigation-submenu-item" onMouseLeave={handleItemMouseLeave}>
          <button type="button" className="Navigation-submenu-link">
            맨즈웨어 <ChevronDown />
          </button>
          <GenderMegaMenu gender="MEN" categories={categories} onLinkClick={handleLinkClick} />
        </div>

        {/* 우먼즈웨어 */}
        <div className="Navigation-submenu-item" onMouseLeave={handleItemMouseLeave}>
          <button type="button" className="Navigation-submenu-link">
            우먼즈웨어 <ChevronDown />
          </button>
          <GenderMegaMenu gender="WOMEN" categories={categories} onLinkClick={handleLinkClick} />
        </div>

        {/* 라이프 */}
        <div className="Navigation-submenu-item" onMouseLeave={handleItemMouseLeave}>
          <button type="button" className="Navigation-submenu-link">
            라이프 <ChevronDown />
          </button>
          <CategoryMegaMenu categoryName="라이프" categories={categories} onLinkClick={handleLinkClick} />
        </div>

        {/* 굿즈 */}
        <div className="Navigation-submenu-item" onMouseLeave={handleItemMouseLeave}>
          <button type="button" className="Navigation-submenu-link">
            굿즈 <ChevronDown />
          </button>
          <CategoryMegaMenu categoryName="굿즈" categories={categories} onLinkClick={handleLinkClick} />
        </div>

        {/* 브랜드 */}
        <div className="Navigation-submenu-item" onMouseLeave={handleItemMouseLeave}>
          <button type="button" className="Navigation-submenu-link">
            브랜드 <ChevronDown />
          </button>
          <div className="Navigation-submenu-megamenu">
            <div className="Navigation-submenu-megamenu-inner">
              <div className="Navigation-submenu-megamenu-col">
                <div className="Navigation-submenu-megamenu-col-title">
                  인기 브랜드
                </div>
                <div className="Navigation-submenu-megamenu-brands-grid">
                  {popularBrands.map((brand) => (
                    <Link key={brand.id} to={brandUrl(brand.name)} onClick={handleLinkClick}>
                      {brand.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 브랜드 랭킹 */}
        <div className="Navigation-submenu-item">
          <NavLink to={brandRankingUrl()} className="Navigation-submenu-link">
            브랜드 랭킹
          </NavLink>
        </div>

        {/* 빈티지 샵 */}
        <div className="Navigation-submenu-item">
          <NavLink to="/vintage-shops" className="Navigation-submenu-link">
            빈티지 샵
          </NavLink>
        </div>

        {/* 기획전 */}
        <div className="Navigation-submenu-item">
          <NavLink to={collectionsUrl()} className="Navigation-submenu-link">
            기획전
          </NavLink>
        </div>
      </div>
    </div>
  );
}
